import React, { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Container from '@mui/material/Container';
import { useGetCriticalIncidentReferrals } from 'api/queries/criticalIncidentQuery';
import {
    type CIReferral,
    CIReferralStatus,
    ReferralsRequestFilterValue
} from 'components/CriticalIncident/interfaces';
import { Spinner } from 'components/Shared';
import { FullScreenMessage } from 'components/Shared/FullScreenMessage';
import { derivePaginationDetails, type ExtendedDataRequest, MaterialTable, MaterialTableFilterBar, Paginator, sortData } from 'components/Shared/MaterialTable';
import { CICard } from '../../Shared/CICard/CICard';
import { useCIReferralsListColumnDefinitions } from '../hooks/useCIReferralsListColumnDefinitions';
import { useScreenSize } from 'components/Layout';
import { MobileCIReferralsList } from '../MobileLayout/MobileCIReferralsList';

import './CIReferralsTable.scss';

export const CIReferralsTable: FC = () => {
    const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });

    const { data: referrals, isLoading } = useGetCriticalIncidentReferrals();

    const columns = useCIReferralsListColumnDefinitions();

    const { isMobile } = useScreenSize();

    const pageSize = isMobile ? 5 : 25;

    const [filteredData, setFilteredData] = useState<CIReferral[] | undefined>(referrals);

    const [dataRequest, setDataRequest] = useState<ExtendedDataRequest<CIReferral>>({
        search: '',
        take: pageSize,
        skip: 0,
        filterConfig: {
            field: 'status',
            options: [
                { label: t('all'), value: ReferralsRequestFilterValue.All },
                { label: t('new'), value: ReferralsRequestFilterValue.New },
                { label: t('pending'), value: ReferralsRequestFilterValue.Pending }
            ],
            value: ReferralsRequestFilterValue.All,
        },
        searchFields: ['externalWorkOrderId'],
        sorts: {
            bookingStartTime: 'asc',
            status: 'desc'
        }
    });

    const sortedData = useMemo(() => sortData(filteredData, dataRequest.sorts), [filteredData, dataRequest.sorts]);

    const pagedData = useMemo(
        () => sortedData?.slice(isMobile ? 0 : dataRequest.skip, dataRequest.skip + dataRequest.take),
        [sortedData, dataRequest.skip, dataRequest.take, isMobile]
    );

    const paginationDetails = derivePaginationDetails(sortedData?.length, dataRequest);

    const emptyStateMessage = useMemo(() => {
        let messageKey = 'you_dont_have_any_ci_referrals';
        if (referrals && referrals.length > 0 && pagedData && pagedData.length === 0) {
            if (dataRequest.filterConfig?.value === ReferralsRequestFilterValue.Pending) {
                messageKey = 'you_dont_have_any_pending_referrals';
            }
            if (dataRequest.filterConfig?.value === ReferralsRequestFilterValue.New) {
                messageKey = 'you_dont_have_any_new_referrals';
            }
            if (dataRequest.search) {
                messageKey = 'no_records_match_work_order_id';
            }
        }
        return t(messageKey);
    }, [t, referrals, pagedData, dataRequest.filterConfig?.value, dataRequest.search, translationsReady]);

    const handlePageChange = (currentPage: number) => {
        setDataRequest({
            ...dataRequest,
            skip: (currentPage - 1) * pageSize,
            take: pageSize
        });
    };

    useEffect(() => {
        setDataRequest((prev) => ({
            ...prev,
            filterConfig: {
                ...prev.filterConfig!,
                options: [
                    { label: t('all'), value: ReferralsRequestFilterValue.All },
                    { label: t('new'), value: ReferralsRequestFilterValue.New },
                    { label: t('pending'), value: ReferralsRequestFilterValue.Pending }
                ]
            },
            take: pageSize,
            skip: 0 //Resets to page 1
        }));
    }, [isMobile, dataRequest.filterConfig?.value, t]);

    return (
        <div className="ci_cases_table_view_wrapper">
            <Container>
                <div className="table_view_title_wrapper">
                    <h1>{t('referrals')}</h1>
                    <MaterialTableFilterBar
                        data={referrals?.filter((referral) => [CIReferralStatus.Open, CIReferralStatus.Accepted].includes(referral.status)) || []}
                        dataRequest={dataRequest}
                        onDataRequestChange={setDataRequest}
                        onFilterData={setFilteredData}
                        searchPlaceholder={t('search_CIReferrals_by_workorder_id_placeholder')}
                    />
                </div>
            </Container>
            {translationsReady ? (
                isMobile ? (
                    <MobileCIReferralsList
                        referrals={pagedData}
                        dataLoading={isLoading}
                        paginationDetails={paginationDetails}
                        handlePageChange={handlePageChange}
                        emptyStateMessage={emptyStateMessage}
                    />
                ) : (
                    <Container>
                        <CICard elevation={2} className="table_container has_filter">
                            <MaterialTable
                                dataLoading={isLoading}
                                columns={columns}
                                data={pagedData}
                                emptyStateMessage={<FullScreenMessage title={emptyStateMessage} />}
                                rowKey="referralId"
                            />
                        </CICard>
                        {pagedData && pagedData.length > 0 && (
                            <Paginator paginationDetails={paginationDetails} onPageChange={handlePageChange} />
                        )}
                    </Container>
                )
            ) : (
                <Spinner fillParent />
            )}
        </div>
    );
};
